import { intersection } from '@/lib/utils/array'
import { useAtom, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo } from 'react'
import {
  referencesProductArticlesFilteredReferencesAtom,
  referencesProductFiltersEnrichedAtom,
  referencesProductIsMonoDiscriminantAtom,
} from '../services/referencesProduct.store'
import type { EnrichedFilter } from '../types/referencesProduct'

const URL_FILTERS_SEPARATOR = '|'

export function useReferencesProductFilters() {
  const [enrichedFilters, setEnrichedFilters] = useAtom(referencesProductFiltersEnrichedAtom)
  const setReferencesProductArticlesFilteredReferences = useSetAtom(
    referencesProductArticlesFilteredReferencesAtom,
  )
  const setIsMonoDiscriminant = useSetAtom(referencesProductIsMonoDiscriminantAtom)

  const totalActivesFilters = useMemo(() => {
    return enrichedFilters.reduce((acc, filter) => acc + filter.valuesChecked.length, 0)
  }, [enrichedFilters])

  const handleVisibleArticlesAndValuesDisabled = useCallback(
    (filters: EnrichedFilter[]) => {
      const activeFilters = filters.filter((filter) => filter.valuesChecked.length > 0)

      if (activeFilters.length === 0) {
        setReferencesProductArticlesFilteredReferences([])
        setEnrichedFilters(
          filters.map((filter) => ({
            ...filter,
            values: filter.values.map((value) => ({ ...value, disabled: false })),
          })),
        )

        return
      }

      // Within each family: OR (union). Between families: AND (intersection).
      const refsPerFamily = new Map<string, Set<string>>()

      for (const filter of activeFilters) {
        refsPerFamily.set(
          filter.label,
          new Set(
            filter.values.filter((v) => v.checked && v.references).flatMap((v) => v.references!),
          ),
        )
      }

      const referencesToDisplay = intersection(...[...refsPerFamily.values()].map((s) => [...s]))
      setReferencesProductArticlesFilteredReferences(referencesToDisplay)

      // disabled: each family checks against intersection of OTHER active families
      const updatedFilters = filters.map((filter) => {
        const otherSets = activeFilters
          .filter((f) => f.label !== filter.label)
          .map((f) => refsPerFamily.get(f.label)!)

        if (otherSets.length === 0) {
          return {
            ...filter,
            values: filter.values.map((value) => ({ ...value, disabled: false })),
          }
        }

        const otherIntersection = new Set(intersection(...otherSets.map((s) => [...s])))

        return {
          ...filter,
          values: filter.values.map((value) => {
            // Never disable a currently-selected value — its selected state must remain visible
            if (filter.valuesChecked.includes(value.label)) {
              return { ...value, disabled: false }
            }

            let disabled = true

            if (value.references) {
              for (const ref of value.references) {
                if (otherIntersection.has(ref)) {
                  disabled = false
                  break
                }
              }
            }

            return { ...value, disabled }
          }),
        }
      })

      setEnrichedFilters(updatedFilters)
    },
    [setEnrichedFilters, setReferencesProductArticlesFilteredReferences],
  )

  const setUrlFromFilters = useCallback((filters: EnrichedFilter[]) => {
    const currentUrl = new URL(window.location.href)

    filters.forEach((filter) => {
      if (filter.valuesChecked.length > 0) {
        currentUrl.searchParams.set(filter.label, filter.valuesChecked.join(URL_FILTERS_SEPARATOR))
      } else {
        currentUrl.searchParams.delete(filter.label)
      }
    })

    window.history.replaceState(null, '', currentUrl.toString())
  }, [])

  const handleUpdateFilters = useCallback(
    (filters: EnrichedFilter[]) => {
      setUrlFromFilters(filters)
      handleVisibleArticlesAndValuesDisabled(filters)
    },
    [setUrlFromFilters, handleVisibleArticlesAndValuesDisabled],
  )

  const setFiltersFromUrl = useCallback(() => {
    //Get all var from url
    const urlParams = new URLSearchParams(window.location.search)
    const urlParamsObject = Object.fromEntries(urlParams)

    //Find position of filter in enrichedFilters and update
    if (Object.keys(urlParamsObject).length > 0) {
      const updatedFilters = enrichedFilters.map((filter) => {
        if (urlParamsObject[filter.label]) {
          const valuesChecked = urlParamsObject[filter.label]!.split(URL_FILTERS_SEPARATOR)
          const updatedValues = filter.values.map((val) => ({
            ...val,
            checked: valuesChecked.includes(val.label),
          }))

          return { ...filter, valuesChecked, values: updatedValues }
        }

        return filter
      })
      handleUpdateFilters(updatedFilters)
    }
  }, [enrichedFilters, handleUpdateFilters])

  const handleFilterChange = useCallback(
    (position: string, value: string | string[]) => {
      const index = parseInt(position)

      const updatedFilters = enrichedFilters.map((filter, i) =>
        i === index ? { ...filter, valuesChecked: [...value] } : filter,
      )

      const updatedValues = updatedFilters[index]!.values.map((val) => ({
        ...val,
        checked: updatedFilters[index]!.valuesChecked.includes(val.label),
      }))
      updatedFilters[index]!.values = updatedValues

      handleUpdateFilters(updatedFilters)
    },
    [enrichedFilters, handleUpdateFilters],
  )

  const handleResetFilters = useCallback(() => {
    const updatedFilters = enrichedFilters.map((filter) => ({ ...filter, valuesChecked: [] }))
    handleUpdateFilters(updatedFilters)
  }, [enrichedFilters, handleUpdateFilters])

  useEffect(() => {
    // Re-apply URL filters on each reset from useEnrichedFilters; URL params absence guards against re-applying after a voluntary user reset.
    if (totalActivesFilters === 0 && enrichedFilters.length > 0) {
      const urlParams = new URLSearchParams(window.location.search)
      const hasMatchingUrlFilters = enrichedFilters.some((filter) => urlParams.has(filter.label))

      if (hasMatchingUrlFilters) setFiltersFromUrl()
    }

    setIsMonoDiscriminant(enrichedFilters.length === 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedFilters])

  return {
    handleFilterChange,
    handleResetFilters,
    totalActivesFilters,
    handleUpdateFilters,
  }
}
